import { MoreVert, PersonAddOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { type FormEvent, useState } from 'react';
import { useNotify } from 'react-admin';
import { formatReportDate, getErrorMessage } from '../utils/helperUtils.ts';
import type { InviteInput, MemberRole, TeamActions, TeamMember } from './teamApi.ts';

type TeamMembersProps = {
  members: TeamMember[];
  actions: TeamActions;
  // Whose row has no actions: nobody changes their own access.
  selfEmail?: string | null;
  onChange: () => void;
};

const whenText = (member: TeamMember) => {
  if (member.status === 'invited') return `Invited ${formatReportDate(member.createdAt)}`;
  if (member.status === 'revoked') return 'No access';
  return member.lastSeenAt ? `Last active ${formatReportDate(member.lastSeenAt)}` : '';
};

/** Who can sign in to a workspace, with the admin's actions on each person. */
const TeamMembers = ({ members, actions, selfEmail, onChange }: TeamMembersProps) => {
  const notify = useNotify();
  const [inviting, setInviting] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; member: TeamMember } | null>(null);
  const [revoking, setRevoking] = useState<TeamMember | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (work: () => Promise<unknown>, done: string) => {
    setMenu(null);
    setBusy(true);
    try {
      await work();
      notify(done, { type: 'success' });
      onChange();
    } catch (error) {
      notify(getErrorMessage(error), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const invite = async (input: InviteInput) => {
    const { emailed } = await actions.invite(input);
    notify(
      emailed
        ? `Invite sent to ${input.email.trim().toLowerCase()}`
        : "The invite is ready, but the email didn't send. Use Resend invite.",
      { type: emailed ? 'success' : 'warning' }
    );
    onChange();
  };

  const open = menu?.member;
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddOutlined />}
          onClick={() => setInviting(true)}
        >
          Invite someone
        </Button>
      </Box>

      <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {members.map((member) => {
          const self = member.email === selfEmail;
          return (
            <Box
              key={member.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                flexWrap: { xs: 'wrap', sm: 'nowrap' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {member.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {member.email}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexShrink: 0 }}>
                {self ? <Chip size="small" label="You" variant="outlined" /> : null}
                <Chip
                  size="small"
                  label={member.role === 'ADMIN' ? 'Admin' : 'User'}
                  color={member.role === 'ADMIN' ? 'primary' : 'default'}
                  variant={member.role === 'ADMIN' ? 'filled' : 'outlined'}
                />
                {member.status === 'invited' ? (
                  <Chip size="small" label="Invited" color="warning" variant="outlined" />
                ) : null}
                {member.status === 'revoked' ? (
                  <Chip size="small" label="Access revoked" color="error" variant="outlined" />
                ) : null}
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: { sm: 150 }, flexShrink: 0 }}
              >
                {whenText(member)}
              </Typography>
              <Box sx={{ width: 40, flexShrink: 0 }}>
                {self ? null : (
                  <IconButton
                    aria-label={`Actions for ${member.fullName}`}
                    disabled={busy}
                    onClick={(event) => setMenu({ anchor: event.currentTarget, member })}
                  >
                    <MoreVert />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {open && open.status !== 'revoked' ? (
          <MenuItem
            onClick={() =>
              void run(
                () => actions.sendPasswordLink(open.id),
                open.status === 'invited'
                  ? `Invite sent again to ${open.email}`
                  : `Password reset sent to ${open.email}`
              )
            }
          >
            {open.status === 'invited' ? 'Resend invite' : 'Send password reset'}
          </MenuItem>
        ) : null}
        {open && open.status !== 'revoked' ? (
          <MenuItem
            onClick={() => {
              const role: MemberRole = open.role === 'ADMIN' ? 'USER' : 'ADMIN';
              void run(
                () => actions.update(open.id, { role }),
                `${open.fullName} is now ${role === 'ADMIN' ? 'an admin' : 'a user'}`
              );
            }}
          >
            {open.role === 'ADMIN' ? 'Make user' : 'Make admin'}
          </MenuItem>
        ) : null}
        {open && open.status === 'revoked' ? (
          <MenuItem
            onClick={() =>
              void run(
                () => actions.update(open.id, { active: true }),
                `${open.fullName} can sign in again`
              )
            }
          >
            Restore access
          </MenuItem>
        ) : null}
        {open && open.status !== 'revoked' ? (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setMenu(null);
              setRevoking(open);
            }}
          >
            Revoke access
          </MenuItem>
        ) : null}
      </Menu>

      <InviteDialog open={inviting} onClose={() => setInviting(false)} onInvite={invite} />

      <Dialog open={Boolean(revoking)} onClose={() => setRevoking(null)}>
        <DialogTitle>Revoke {revoking?.fullName}&apos;s access?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They are signed out on their next click and cannot sign in until you restore access.
            Their records stay in Forge.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => {
              const member = revoking;
              setRevoking(null);
              if (member) {
                void run(
                  () => actions.update(member.id, { active: false }),
                  `${member.fullName} can no longer sign in`
                );
              }
            }}
          >
            Revoke access
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

type InviteDialogProps = {
  open: boolean;
  onClose: () => void;
  onInvite: (input: InviteInput) => Promise<void>;
};

const InviteDialog = ({ open, onClose, onInvite }: InviteDialogProps) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('USER');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const close = () => {
    setFullName('');
    setEmail('');
    setRole('USER');
    setError(null);
    onClose();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!fullName.trim() || !email.includes('@')) {
      setError('Add their name and email.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onInvite({ fullName, email, role });
      close();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle>Invite someone</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            They get an email with a link to set their password. The link works for 7 days.
          </DialogContentText>
          <TextField
            label="Full name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            autoFocus
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            margin="dense"
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value as MemberRole)}
            fullWidth
            margin="dense"
            helperText={
              role === 'ADMIN'
                ? 'Admins also invite people and change who has access.'
                : 'Users work in Forge but do not manage the team.'
            }
          >
            <MenuItem value="USER">User</MenuItem>
            <MenuItem value="ADMIN">Admin</MenuItem>
          </TextField>
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TeamMembers;
